import { useWorkspaceStore } from './store';
import { workspace } from './controller';
import { getTabStripEl, getWorkspaceEl } from './refs';
import { detectSnap, snapRect } from './snap';
import type { Rect, SnapRegion, WorkspacePanelState } from './types';

type TargetKind = 'tab' | SnapRegion | null;

/** Pointer travel (px) before a floating/snapped header-grab actually starts
 * moving — a dead zone so a plain click on a snapped window's header focuses it
 * without un-snapping (which would jump the whole dock back to full width). */
const DRAG_THRESHOLD = 4;

const rectOf = (el: HTMLElement | null): DOMRect => el?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
const clampAxis = (v: number, size: number, avail: number) => Math.max(0, Math.min(v, Math.max(0, avail - size)));

/**
 * Start a panel drag from a header/handle. Works for both a floating window's
 * toolbar and a tab (which auto-detaches into floating). Handles drop-target
 * priority, live previews, bounds clamping, and Escape-to-cancel.
 *
 * Game-engine loop (see DOCK_CONTRACT.md §7): ALL static geometry — the stage
 * rect, the tab-strip hit rect, the snap area — is measured ONCE at drag start
 * into plain numbers. During the drag the dragged window is moved by writing
 * `left`/`top` straight onto its DOM node (no store write, so no React
 * re-render of the panel tree) and the drop target is resolved by cheap math
 * against that precomputed geometry (no per-frame `getBoundingClientRect`).
 * Writing the live position (rather than a transform offset) keeps the node's
 * `left`/`top` in sync with where it visually is, so committing on drop — or a
 * snap animating into place — starts from the drop point, never snapping back
 * to the origin first. The only store writes mid-drag are the drop-preview
 * overlay, and only when the target REGION actually changes. The real
 * reorder/dock/snap is committed exactly once, on drag END.
 *
 * Drop priority: (1) tab organizer → dock · (2) corner → quarter · (3) edge →
 * half · (4) elsewhere → floating.
 */
export function startPanelDrag(
  panelId: string,
  ev: { clientX: number; clientY: number },
  opts: { fromTab?: boolean } = {},
): void {
  const store = useWorkspaceStore;
  const panel = store.getState().panels[panelId];
  if (!panel) return;
  const before: WorkspacePanelState = { ...panel };

  // A maximized panel sits in a z-index tier above the whole dock canvas
  // (DOCK_CONTRACT.md) — including .wp-stage, where a dragged/floating panel
  // and its snap preview live — so it fully covers them, both visually and
  // for pointer events. The only way to reach this handler while something
  // else is maximized is via the tab strip (still reachable above it; see
  // WorkspaceSurface.tsx / workspace.css), so restore it first: otherwise
  // the drag that's about to start would be invisible until it lands.
  const maximizedId = store.getState().maximizedId;
  if (maximizedId && maximizedId !== panelId) workspace.restorePanel(maximizedId);

  // ── Precompute ALL static geometry ONCE — nothing below reads the DOM again.
  const stage = rectOf(getWorkspaceEl());
  const strip = rectOf(getTabStripEl());
  const ws: Rect = { x: 0, y: 0, width: stage.width, height: stage.height };

  let width: number;
  let height: number;
  let grabX: number;
  let grabY: number;
  if (opts.fromTab || before.mode === 'tabbed' || before.width == null) {
    const size = workspace.defaultSize(before.type);
    width = size.width;
    height = size.height;
    grabX = Math.min(64, Math.round(width * 0.2)); // anchor pointer near the header's start
    grabY = 14;
  } else {
    width = before.width;
    height = before.height!;
    grabX = ev.clientX - stage.left - (before.x ?? 0);
    grabY = ev.clientY - stage.top - (before.y ?? 0);
  }

  // Pure pointer→clamped-floating-position math (uses only precomputed numbers).
  const posFor = (clientX: number, clientY: number) => ({
    x: clampAxis(clientX - stage.left - grabX, width, ws.width),
    y: clampAxis(clientY - stage.top - grabY, height, ws.height),
  });

  const start = posFor(ev.clientX, ev.clientY);
  // A tab that already crossed its detach threshold (fromTab) or a tabbed panel
  // floats immediately; a header-grab on an existing floating/snapped window
  // waits for real movement (DRAG_THRESHOLD) so a plain click just focuses it.
  const detachNow = opts.fromTab || before.mode === 'tabbed' || before.width == null;

  // The dragged window's DOM node — resolved lazily (a fromTab detach mounts it
  // on the next React commit) and cached, then moved directly each frame.
  let node: HTMLElement | null = null;
  const nodeEl = (): HTMLElement | null =>
    (node ??= document.querySelector<HTMLElement>(`.wp-window[data-panel-id="${panelId}"]`));

  // Float in place exactly once (ONE store write) — then every frame moves the
  // window by transform only. Idempotent so the dead-zone check can call it.
  let floating = false;
  const ensureFloating = () => {
    if (floating) return;
    floating = true;
    workspace.beginFloatDrag(panelId, { x: start.x, y: start.y, width, height });
    workspace.setDraggingId(panelId);
  };
  if (detachNow) ensureFloating();
  else workspace.focusPanel(panelId); // bring to front; float only once it moves

  let targetKind: TargetKind = null;
  let targetRect: Rect = { x: start.x, y: start.y, width, height };
  let previewKey = 'none'; // dedup: only write the store when the region changes

  const setPreview = (kind: TargetKind, rect?: Rect): void => {
    const key = kind ?? 'none';
    if (key === previewKey) return;
    previewKey = key;
    if (kind === 'tab') {
      store.setState({ snapPreview: { kind: 'tab', rect: { x: 0, y: 0, width: ws.width, height: 0 } } });
    } else if (kind) {
      store.setState({ snapPreview: { kind, rect: rect! } });
    } else {
      store.setState({ snapPreview: null });
    }
  };

  const onMove = (e: PointerEvent) => {
    e.preventDefault();
    // Dead zone: a header-grab only becomes a move once the pointer really
    // travels — a plain click leaves a snapped/floating window untouched.
    if (!floating) {
      if (Math.abs(e.clientX - ev.clientX) < DRAG_THRESHOLD && Math.abs(e.clientY - ev.clientY) < DRAG_THRESHOLD) return;
      ensureFloating();
    }
    const localX = e.clientX - stage.left;
    const localY = e.clientY - stage.top;
    const pos = posFor(e.clientX, e.clientY);

    // Follow the pointer by writing left/top straight onto the node — no store
    // write, no React re-render of the panel tree (the store still holds the
    // pre-drag position, so React writes nothing over these until we commit).
    const el = nodeEl();
    if (el) {
      el.style.left = `${pos.x}px`;
      el.style.top = `${pos.y}px`;
    }

    // (1) Tab dock — cheap point-in-rect against the PRECOMPUTED strip rect.
    // Only the explicit tab-strip overlay qualifies; a document/panel body
    // never does.
    if (strip.width > 0 && e.clientX >= strip.left && e.clientX <= strip.right && e.clientY >= strip.top && e.clientY <= strip.bottom) {
      targetKind = 'tab';
      setPreview('tab');
      return;
    }

    // (2)/(3) corner + edge snapping (corners take priority inside detectSnap).
    const region = detectSnap(localX, localY, ws);
    if (region) {
      targetKind = region;
      targetRect = snapRect(region, ws);
      setPreview(region, targetRect);
    } else {
      // (4) floating
      targetKind = null;
      targetRect = { x: pos.x, y: pos.y, width, height };
      setPreview(null);
    }
  };

  const cleanup = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('keydown', onKey);
    // Leave the node's live left/top as-is — the commit below (or applyState on
    // cancel) re-renders it from the store, so a snap animates from the drop
    // point and a float stays exactly where it was released. No jump-to-origin.
    store.setState({ snapPreview: null });
    workspace.setDraggingId(null);
  };

  const onUp = () => {
    cleanup();
    if (!floating) return; // a plain click on the header — focus only, no move
    if (targetKind === 'tab') workspace.tabPanel(panelId);
    else if (targetKind) workspace.snapPanel(panelId, targetKind, ws);
    else workspace.commitGeometry(panelId, targetRect);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    cleanup();
    if (!floating) return;
    workspace.applyState(panelId, before);
    // React may skip re-writing left/top when the restored value equals what it
    // last rendered (the pre-drag position) — but we moved the node directly, so
    // force it back to the restored geometry so cancel truly reverts.
    const el = nodeEl();
    if (el && before.x != null && before.y != null) {
      el.style.left = `${before.x}px`;
      el.style.top = `${before.y}px`;
    }
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('keydown', onKey);
}
